import { useEffect, useState } from 'react';
import {
  PRIVACY_ID_TO_FIELD_VISIBILITY,
  PRIVACY_LEVEL,
  toPrivacyLevel,
  type PrivacyLevel,
} from '@wlb/shared/constants/privacy';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { Profile } from '@wlb/shared/types';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import {
  CameraFilledIcon,
  ChevronLeftIcon,
  EditFilledIcon,
  EyeFilledIcon,
} from '../../components/ui/icons';
import { Modal } from '../../components/ui/Modal';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import { getProfileAvatarUrl } from './useCurrentProfile';
import './profile.css';

interface ProfilePhotoModalProps {
  profile: Profile;
  canManage?: boolean;
  onClose: () => void;
  onEditPhoto?: () => void;
  onUpdatePhoto?: () => void;
}

const PRIVACY_CHOICES: Array<{ level: PrivacyLevel; titleKey: string; descriptionKey: string }> = [
  {
    level: PRIVACY_LEVEL.PUBLIC,
    titleKey: 'profile.privacy.options.public.title',
    descriptionKey: 'profile.privacy.options.public.description',
  },
  {
    level: PRIVACY_LEVEL.BUDDIES,
    titleKey: 'profile.privacy.options.buddies.title',
    descriptionKey: 'profile.privacy.options.buddies.description',
  },
  {
    level: PRIVACY_LEVEL.PRIVATE,
    titleKey: 'profile.privacy.options.private.title',
    descriptionKey: 'profile.privacy.options.private.description',
  },
];

export function ProfilePhotoModal({
  profile,
  canManage = false,
  onClose,
  onEditPhoto,
  onUpdatePhoto,
}: ProfilePhotoModalProps) {
  const { t } = useTranslation();
  const { updateProfile } = useProfile();
  const savedPrivacy =
    toPrivacyLevel(profile.profilePicturePrivacy) ?? PRIVACY_LEVEL.PUBLIC;
  const [privacy, setPrivacy] = useState<PrivacyLevel>(savedPrivacy);
  const [privacyDraft, setPrivacyDraft] = useState<PrivacyLevel>(savedPrivacy);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [error, setError] = useState('');
  const avatarUrl = getProfileAvatarUrl(profile);
  const username = getUsernameDisplay(profile);

  useEffect(() => {
    setPrivacy(savedPrivacy);
    setPrivacyDraft(savedPrivacy);
  }, [savedPrivacy]);

  const savePrivacy = async (): Promise<void> => {
    if (privacyDraft === privacy || savingPrivacy) {
      setShowPrivacy(false);
      return;
    }

    setSavingPrivacy(true);
    setError('');
    try {
      await updateProfile({
        profilePicturePrivacy: PRIVACY_ID_TO_FIELD_VISIBILITY[privacyDraft],
      });
      setPrivacy(privacyDraft);
      setShowPrivacy(false);
      ToastService.show({
        type: 'success',
        message: t('profile.photoViewer.privacyUpdated'),
      });
    } catch {
      setPrivacyDraft(privacy);
      setError(t('profile.photoViewer.privacyFailed'));
    } finally {
      setSavingPrivacy(false);
    }
  };

  const currentPrivacy = PRIVACY_CHOICES.find(choice => choice.level === privacy)
    ?? PRIVACY_CHOICES[0];

  return (
    <Modal
      title={
        showPrivacy ? (
          <span className="profile-photo-viewer__title">
            <button
              type="button"
              className="profile-photo-viewer__back"
              aria-label={t('common.buttons.back')}
              onClick={() => {
                setPrivacyDraft(privacy);
                setError('');
                setShowPrivacy(false);
              }}
            >
              <ChevronLeftIcon size={20} />
            </button>
            {t('profile.photoViewer.visibilityTitle')}
          </span>
        ) : t('profile.photoViewer.title')
      }
      labelledBy="profile-photo-title"
      className="profile-photo-modal"
      onClose={onClose}
    >
      {showPrivacy ? (
        <div className="profile-photo-visibility">
          <p>{t('profile.photoViewer.visibilityDescription')}</p>
          <div className="profile-photo-visibility__options" role="radiogroup">
            {PRIVACY_CHOICES.map(choice => {
              const selected = privacyDraft === choice.level;
              return (
                <button
                  key={choice.level}
                  type="button"
                  role="radio"
                  className="profile-photo-visibility__option"
                  aria-checked={selected}
                  disabled={savingPrivacy}
                  onClick={() => setPrivacyDraft(choice.level)}
                >
                  <span
                    className={`profile-photo-visibility__radio${selected ? ' profile-photo-visibility__radio--selected' : ''}`}
                    aria-hidden
                  />
                  <span>
                    <strong>{t(choice.titleKey)}</strong>
                    <small>{t(choice.descriptionKey)}</small>
                  </span>
                </button>
              );
            })}
          </div>
          <FormError variant="banner" message={error} />
          <div className="profile-photo-visibility__footer">
            <Button
              onClick={() => {
                savePrivacy().catch(() => undefined);
              }}
              disabled={savingPrivacy}
            >
              {t(savingPrivacy ? 'profile.photoViewer.savingPrivacy' : 'common.buttons.save')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="profile-photo-viewer">
          <img
            className="profile-photo-viewer__image"
            src={avatarUrl}
            alt={t('profile.photoViewer.imageAlt', { name: username })}
          />

          {canManage && (
            <div className="profile-photo-viewer__toolbar">
              <button
                type="button"
                className="profile-photo-viewer__visibility-pill"
                onClick={() => setShowPrivacy(true)}
              >
                <EyeFilledIcon size={19} />
                {t(currentPrivacy.titleKey)}
              </button>
              <div className="profile-photo-viewer__actions">
                <button
                  type="button"
                  className="profile-photo-viewer__action"
                  onClick={onEditPhoto}
                >
                  <EditFilledIcon size={28} />
                  <span>{t('profile.photoViewer.editPhoto')}</span>
                </button>
                <button
                  type="button"
                  className="profile-photo-viewer__action"
                  onClick={onUpdatePhoto}
                >
                  <CameraFilledIcon size={28} />
                  <span>{t('profile.photoViewer.updatePhoto')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
